import React, { useState, useEffect, useCallback } from 'react';
import {
  Cpu,
  RefreshCw,
  Trash2,
  Power,
  PowerOff,
  Unlock,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronRight,
  Edit2,
  Hash,
  X,
  Wand2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useSocket } from '../contexts/SocketContext';

const onlineDot = (online) =>
  `inline-block w-2 h-2 rounded-full ${online ? 'bg-success-400' : 'bg-danger-500'}`;

const Controllers = () => {
  const { socket } = useSocket();
  const [controllers, setControllers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState({});
  // Edit modal state — open via the pencil icon next to a controller
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', hostname: '', group_id: 'auto' });
  const [editSaving, setEditSaving] = useState(false);
  // Bulk-rename modal state
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameForm, setRenameForm] = useState({
    prefix: 'Locker #',
    startNumber: 1,
    startChannel: 1
  });
  const [renameSaving, setRenameSaving] = useState(false);

  const fetchControllers = useCallback(async () => {
    try {
      const res = await axios.get('/api/controllers');
      setControllers(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch controllers', err);
      toast.error('Failed to load controllers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get('/api/locker-groups');
      setGroups(res.data || []);
    } catch (err) {
      console.error('Failed to fetch groups', err);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchControllers();
  }, [fetchControllers]);

  // Live updates from the backend Aywana handler
  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchControllers();
    socket.on('controller-discovered', refresh);
    socket.on('locker-discovered', refresh);
    socket.on('controller-availability', refresh);
    socket.on('controller-telemetry', refresh);
    socket.on('locker-state-changed', refresh);
    return () => {
      socket.off('controller-discovered', refresh);
      socket.off('locker-discovered', refresh);
      socket.off('controller-availability', refresh);
      socket.off('controller-telemetry', refresh);
      socket.off('locker-state-changed', refresh);
    };
  }, [socket, fetchControllers]);

  const setBusyKey = (key, value) =>
    setBusy((prev) => ({ ...prev, [key]: value }));

  const sendRelayCommand = async (controllerId, channel, action) => {
    const key = `${controllerId}-${channel}-${action}`;
    setBusyKey(key, true);
    try {
      const res = await axios.post(
        `/api/controllers/${controllerId}/relay/${channel}`,
        { action }
      );
      if (res.data?.success) {
        toast.success(
          action === 'open'
            ? `Pulsed channel ${channel}`
            : `Channel ${channel} -> ${action.toUpperCase()}`
        );
      } else {
        toast.error(res.data?.error || `Command failed`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(`Relay command failed: ${msg}`);
    } finally {
      setBusyKey(key, false);
    }
  };

  const removeController = async (controller) => {
    if (
      !window.confirm(
        `Remove controller "${controller.hostname}"? Its lockers stay in the system but get unlinked from this hub.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`/api/controllers/${controller.id}`);
      toast.success(`Forgot ${controller.hostname}`);
      fetchControllers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete controller');
    }
  };

  const openEdit = (controller) => {
    setEditTarget(controller);
    setEditForm({
      name: controller.name || '',
      hostname: controller.hostname || '',
      group_id: controller.group ? String(controller.group.id) : 'auto'
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const body = {};
      if (editForm.name !== (editTarget.name || '')) body.name = editForm.name;
      if (editForm.hostname && editForm.hostname !== editTarget.hostname) {
        body.hostname = editForm.hostname;
      }
      // Always send group_id so a no-op selection still confirms intent.
      body.group_id =
        editForm.group_id === 'auto'
          ? 'auto'
          : editForm.group_id
            ? Number(editForm.group_id)
            : null;
      await axios.put(`/api/controllers/${editTarget.id}`, body);
      toast.success('Controller updated');
      setEditTarget(null);
      fetchControllers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update controller');
    } finally {
      setEditSaving(false);
    }
  };

  const runSyncGroup = async (controller) => {
    try {
      const res = await axios.post(`/api/controllers/${controller.id}/sync-group`);
      const detected = res.data?.detected;
      toast.success(
        detected
          ? `Re-detected group "${detected.name}"`
          : 'No group hint in hostname; group cleared'
      );
      fetchControllers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sync failed');
    }
  };

  const openRename = (controller) => {
    const prefix = controller.group ? `${controller.group.name} #` : 'Locker #';
    setRenameTarget(controller);
    setRenameForm({ prefix, startNumber: 1, startChannel: 1 });
  };

  const saveRename = async () => {
    if (!renameTarget) return;
    setRenameSaving(true);
    try {
      const res = await axios.post(
        `/api/controllers/${renameTarget.id}/rename-channels`,
        {
          prefix: renameForm.prefix,
          startNumber: Number(renameForm.startNumber) || 1,
          startChannel: Number(renameForm.startChannel) || 1
        }
      );
      const count = res.data?.renamed?.length || 0;
      toast.success(`Renamed ${count} channel${count === 1 ? '' : 's'}`);
      setRenameTarget(null);
      fetchControllers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rename failed');
    } finally {
      setRenameSaving(false);
    }
  };

  const toggleExpanded = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="p-6 text-gray-300 flex items-center space-x-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Loading controllers...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center space-x-2">
            <Cpu className="w-6 h-6" />
            <span>Controllers</span>
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Multi-channel relay boards (Aywana / ESP32). They are
            auto-discovered when they publish their MQTT discovery
            messages and each channel becomes a locker.
          </p>
        </div>
        <button
          onClick={fetchControllers}
          className="flex items-center space-x-2 bg-dashboard-700 hover:bg-dashboard-600 text-white px-3 py-2 rounded-lg transition"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {controllers.length === 0 && (
        <div className="border border-dashboard-700 rounded-xl p-6 text-center text-gray-400 bg-dashboard-800">
          No controllers discovered yet. Power on an Aywana board pointed at
          the same MQTT broker and it should appear within a few seconds of
          publishing its <code className="text-primary-400">homeassistant/switch/.../config</code> messages.
        </div>
      )}

      <div className="space-y-4">
        {controllers.map((controller) => {
          const isOpen = expanded[controller.id] !== false; // default open
          const lockers = controller.lockers || [];
          return (
            <div
              key={controller.id}
              className="bg-dashboard-800 border border-dashboard-700 rounded-xl shadow-card"
            >
              <button
                onClick={() => toggleExpanded(controller.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-dashboard-700/40 transition"
              >
                <div className="flex items-center space-x-3 text-left">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                  <div className="w-10 h-10 bg-primary-600/20 border border-primary-600 rounded-lg flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-semibold">
                        {controller.name || controller.hostname}
                      </span>
                      <span className={onlineDot(controller.is_online)} />
                      {controller.is_online ? (
                        <Wifi className="w-3.5 h-3.5 text-success-400" />
                      ) : (
                        <WifiOff className="w-3.5 h-3.5 text-danger-400" />
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {controller.hostname}
                      {controller.model ? ` - ${controller.model}` : ''}
                      {controller.sw_version ? ` - fw ${controller.sw_version}` : ''}
                      {controller.ip_address ? ` - ${controller.ip_address}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span
                    className="text-xs px-2 py-0.5 rounded border"
                    style={
                      controller.group
                        ? {
                            backgroundColor: `${controller.group.color || '#6B7280'}26`,
                            borderColor: `${controller.group.color || '#6B7280'}66`,
                            color: controller.group.color || '#9CA3AF'
                          }
                        : { backgroundColor: 'transparent', borderColor: '#374151', color: '#9CA3AF' }
                    }
                    title={
                      controller.group
                        ? `Channels belong to the "${controller.group.name}" group`
                        : 'No group; click the wand to detect from hostname or edit to assign'
                    }
                  >
                    {controller.group ? controller.group.name : 'No group'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {controller.online_lockers}/{controller.total_lockers} online
                  </span>
                  <span className="text-xs text-gray-500 px-2 py-0.5 rounded bg-dashboard-700 uppercase tracking-wide">
                    {controller.protocol || 'aywana'}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      runSyncGroup(controller);
                    }}
                    title="Re-detect group from hostname"
                    className="text-gray-400 hover:text-primary-400 transition p-1"
                  >
                    <Wand2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openRename(controller);
                    }}
                    title="Bulk-rename channels"
                    className="text-gray-400 hover:text-primary-400 transition p-1"
                  >
                    <Hash className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(controller);
                    }}
                    title="Edit controller"
                    className="text-gray-400 hover:text-primary-400 transition p-1"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeController(controller);
                    }}
                    title="Forget controller"
                    className="text-gray-400 hover:text-danger-400 transition p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-dashboard-700 p-4">
                  {lockers.length === 0 ? (
                    <div className="text-sm text-gray-400">
                      Discovery received but no channels yet. They appear as
                      relay/state messages arrive on the broker.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {lockers
                        .slice()
                        .sort((a, b) => (a.channel || 0) - (b.channel || 0))
                        .map((locker) => {
                          const isOn = (locker.relay_state || 'OFF') === 'ON';
                          const k = (a) =>
                            `${controller.id}-${locker.channel}-${a}`;
                          return (
                            <div
                              key={locker.id}
                              className="border border-dashboard-700 rounded-lg p-3 bg-dashboard-900"
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-white">
                                  Ch {locker.channel}
                                </div>
                                <div className="flex items-center space-x-2 text-xs">
                                  <span className={onlineDot(locker.is_online)} />
                                  <span
                                    className={`px-1.5 py-0.5 rounded ${
                                      isOn
                                        ? 'bg-success-600/20 text-success-300'
                                        : 'bg-dashboard-700 text-gray-400'
                                    }`}
                                  >
                                    {locker.relay_state || 'OFF'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 truncate mt-1" title={locker.name}>
                                {locker.name}
                              </div>
                              <div className="grid grid-cols-3 gap-2 mt-3">
                                <button
                                  disabled={busy[k('open')]}
                                  onClick={() =>
                                    sendRelayCommand(
                                      controller.id,
                                      locker.channel,
                                      'open'
                                    )
                                  }
                                  className="flex items-center justify-center space-x-1 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-xs py-1.5 rounded"
                                  title="Pulse open"
                                >
                                  <Unlock className="w-3.5 h-3.5" />
                                  <span>Open</span>
                                </button>
                                <button
                                  disabled={busy[k('on')]}
                                  onClick={() =>
                                    sendRelayCommand(
                                      controller.id,
                                      locker.channel,
                                      'on'
                                    )
                                  }
                                  className="flex items-center justify-center space-x-1 bg-success-600/30 hover:bg-success-600/50 disabled:opacity-50 text-success-200 text-xs py-1.5 rounded"
                                  title="Sticky ON"
                                >
                                  <Power className="w-3.5 h-3.5" />
                                  <span>On</span>
                                </button>
                                <button
                                  disabled={busy[k('off')]}
                                  onClick={() =>
                                    sendRelayCommand(
                                      controller.id,
                                      locker.channel,
                                      'off'
                                    )
                                  }
                                  className="flex items-center justify-center space-x-1 bg-dashboard-700 hover:bg-dashboard-600 disabled:opacity-50 text-gray-300 text-xs py-1.5 rounded"
                                  title="Sticky OFF"
                                >
                                  <PowerOff className="w-3.5 h-3.5" />
                                  <span>Off</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit controller modal */}
      {editTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-dashboard-800 border border-dashboard-700 rounded-xl shadow-card w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                Edit {editTarget.hostname}
              </h2>
              <button
                onClick={() => setEditTarget(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-xs text-gray-400 mb-1">Display name</label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3"
              placeholder={editTarget.hostname}
            />

            <label className="block text-xs text-gray-400 mb-1">
              Hostname (MQTT topic prefix)
            </label>
            <input
              type="text"
              value={editForm.hostname}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, hostname: e.target.value }))
              }
              className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3 font-mono text-sm"
            />
            <p className="text-xs text-gray-500 -mt-2 mb-3">
              Putting <code className="text-primary-400">men</code> or{' '}
              <code className="text-primary-400">women</code> in the hostname
              auto-assigns every channel to that group.
            </p>

            <label className="block text-xs text-gray-400 mb-1">Group</label>
            <select
              value={editForm.group_id}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, group_id: e.target.value }))
              }
              className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-4"
            >
              <option value="auto">Auto-detect from hostname</option>
              {groups.map((g) => (
                <option key={g.id} value={String(g.id)}>{g.name}</option>
              ))}
              <option value="">No group</option>
            </select>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setEditTarget(null)}
                className="px-3 py-2 text-sm text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={editSaving}
                className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg"
              >
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-rename modal */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-dashboard-800 border border-dashboard-700 rounded-xl shadow-card w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                Renumber channels — {renameTarget.hostname}
              </h2>
              <button
                onClick={() => setRenameTarget(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              Sets the locker name for each channel from{' '}
              <code className="text-primary-400">startChannel</code> upward to{' '}
              <code className="text-primary-400">prefix + N</code>, where{' '}
              <code className="text-primary-400">N</code> starts at{' '}
              <code className="text-primary-400">startNumber</code> and counts up.
            </p>

            <label className="block text-xs text-gray-400 mb-1">Prefix</label>
            <input
              type="text"
              value={renameForm.prefix}
              onChange={(e) =>
                setRenameForm((f) => ({ ...f, prefix: e.target.value }))
              }
              className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white mb-3 font-mono text-sm"
            />

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Start number
                </label>
                <input
                  type="number"
                  min="1"
                  value={renameForm.startNumber}
                  onChange={(e) =>
                    setRenameForm((f) => ({ ...f, startNumber: e.target.value }))
                  }
                  className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Start at channel
                </label>
                <input
                  type="number"
                  min="1"
                  value={renameForm.startChannel}
                  onChange={(e) =>
                    setRenameForm((f) => ({ ...f, startChannel: e.target.value }))
                  }
                  className="w-full bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>

            <div className="bg-dashboard-900 border border-dashboard-700 rounded-lg px-3 py-2 mb-4 text-xs">
              <span className="text-gray-400">Preview: </span>
              <span className="text-white font-mono">
                {`${renameForm.prefix}${renameForm.startNumber}`},{' '}
                {`${renameForm.prefix}${Number(renameForm.startNumber) + 1}`}, ...
              </span>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setRenameTarget(null)}
                className="px-3 py-2 text-sm text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveRename}
                disabled={renameSaving}
                className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg"
              >
                {renameSaving ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Controllers;
